/**
 * Render an ISO timestamp as a short relative-time string ("5m ago", "2h ago",
 * "3d ago"). Uses the browser's built-in `Intl.RelativeTimeFormat` rather
 * than pulling in `date-fns`; once the formatting needs grow (locale-specific
 * "yesterday at 14:32" etc.) we can revisit.
 *
 * The unit thresholds are deliberately coarse — the conversation list cares
 * about "how stale is this row?", not exact arithmetic. For times under a
 * minute we return `"now"` to avoid a flickering "0s ago".
 */
const RTF = new Intl.RelativeTimeFormat('en', { numeric: 'auto', style: 'short' });

export function formatRelativeTime(input: string | Date | null | undefined): string {
  if (input == null) {
    return '';
  }
  const ts = typeof input === 'string' ? Date.parse(input) : input.getTime();
  if (Number.isNaN(ts)) {
    return '';
  }

  const deltaMs = ts - Date.now();
  const absSeconds = Math.abs(deltaMs) / 1000;

  if (absSeconds < 60) {
    return 'now';
  }
  if (absSeconds < 60 * 60) {
    return RTF.format(Math.round(deltaMs / 1000 / 60), 'minute');
  }
  if (absSeconds < 60 * 60 * 24) {
    return RTF.format(Math.round(deltaMs / 1000 / 60 / 60), 'hour');
  }
  if (absSeconds < 60 * 60 * 24 * 30) {
    return RTF.format(Math.round(deltaMs / 1000 / 60 / 60 / 24), 'day');
  }
  if (absSeconds < 60 * 60 * 24 * 365) {
    return RTF.format(Math.round(deltaMs / 1000 / 60 / 60 / 24 / 30), 'month');
  }
  return RTF.format(Math.round(deltaMs / 1000 / 60 / 60 / 24 / 365), 'year');
}
